import { useCallback, useEffect, useState } from "react"
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"
import API from "@/common/API"
import { Routes } from "@/routes"
import UsageStatisticsComponent from "@/components/platform/UsageStatisticsComponent"
import VisitReviewComponent from "@/components/platform/VisitReviewComponent"
import SyncMonitorComponent from "@/components/platform/SyncMonitorComponent"
import IndicatorsPage from "@/pages/indicators/IndicatorsPage"
import ActivityLogPage from "@/pages/ActivityLogPage"
import VehicleStopsPage from "@/pages/VehicleStopsPage"
import DispatchIndicatorsPage from "@/pages/indicators/DispatchIndicatorsPage"
import SalesBySellerPage from "@/pages/SalesBySellerPage"
import SalesByMonthPage from "@/pages/SalesByMonthPage"
import DispatchTopProductsBySellerPage from "@/pages/indicators/DispatchTopProductsBySellerPage"
import DispatchCategoryDrilldownPage from "@/pages/indicators/DispatchCategoryDrilldownPage"
import DispatchByDependencyPage from "@/pages/indicators/DispatchByDependencyPage"
import OrgDetailsModal from "@/components/Organizations/OrgDetailsModal"
import EmailLogComponent from "@/components/platform/EmailLogComponent"

interface PlatformStats {
  totalVisits: number
  activeAgents: number
  totalStops: number
  placesMatched: number
}

interface OrgRecord {
  id: string
  name: string
  active: boolean
  totalPlaces: number
  totalUsers: number
  totalVehicles: number
}

type Tab = "overview" | "organizations" | "vehicleStops" | "activityLog" | "emailLog" | "usage" | "visitReview" | "sync" | "dispatch" | "salesBySeller" | "salesByMonth" | "topProductsBySeller" | "categoryDrilldown" | "byDependency"

const VALID_TABS: Tab[] = [
  "overview", "organizations", "vehicleStops", "activityLog", "emailLog",
  "usage", "visitReview", "sync", "dispatch", "salesBySeller",
  "salesByMonth", "topProductsBySeller", "categoryDrilldown", "byDependency"
]

const PlatformPage = () => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const location = useLocation()
  const user = parsedUser()

  const getTabFromUrl = useCallback((): Tab => {
    const params = new URLSearchParams(location.search)
    const tabParam = params.get("tab") as Tab
    return VALID_TABS.includes(tabParam) ? tabParam : "overview"
  }, [location.search])

  const [activeTab, setActiveTabState] = useState<Tab>(getTabFromUrl)
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [orgs, setOrgs] = useState<OrgRecord[]>([])
  const [selectedOrgForDetail, setSelectedOrgForDetail] = useState<OrgRecord | null>(null)
  const [orgsPage, setOrgsPage] = useState(0)
  const [orgsTotalPages, setOrgsTotalPages] = useState(0)
  const [orgsSearch, setOrgsSearch] = useState("")
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)

  // Guard — redirect non-superadmin users
  useEffect(() => {
    if (!isSuperAdmin(user)) {
      history.replace(Routes.Home.path)
    }
  }, [])

  // Sync state if URL search changes externally (e.g. browser navigation)
  useEffect(() => {
    const tab = getTabFromUrl()
    if (tab !== activeTab) {
      setActiveTabState(tab)
    }
  }, [location.search, getTabFromUrl])

  const handleTabChange = (tab: Tab) => {
    setActiveTabState(tab)
    const params = new URLSearchParams(location.search)
    params.set("tab", tab)
    history.push({ search: params.toString() })
  }

  const loadStats = async () => {
    try {
      setIsLoadingStats(true)
      const res = await API.getAction("Platform/Stats")
      if (res.data.status === "ok") {
        setStats(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingStats(false)
    }
  }

  const loadOrgs = async (page = 0, search = orgsSearch) => {
    try {
      setIsLoadingOrgs(true)
      const qs = `Page=${page}&Quantity=20${search ? `&Search=${encodeURIComponent(search)}` : ""}`
      const res = await API.getAction("Organization/AdminList", qs)
      if (res.data.status === "ok") {
        setOrgs(res.data.response)
        setOrgsTotalPages(res.data.quantityOfPages)
        setOrgsPage(page)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrgs(false)
    }
  }

  useEffect(() => {
    loadStats()
  }, [])

  // Debounce search on orgs
  useEffect(() => {
    const timer = setTimeout(() => {
      loadOrgs(0, orgsSearch)
    }, 350)
    return () => clearTimeout(timer)
  }, [orgsSearch])

  const kpiCards = [
    { label: t("Total Visits"), value: stats?.totalVisits, icon: "fa-map-marker-alt", color: "primary" },
    { label: t("Active Agents"), value: stats?.activeAgents, icon: "fa-users", color: "success" },
    { label: t("Vehicle Stops"), value: stats?.totalStops, icon: "fa-map-pin", color: "warning" },
    { label: t("Places Matched"), value: stats?.placesMatched, icon: "fa-check-circle", color: "info" },
  ]

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: "overview", label: t("Overview"), icon: "fa-gauge-high" },
    { key: "organizations", label: t("Organizations"), icon: "fa-building" },
    { key: "vehicleStops", label: t("Vehicle Stops"), icon: "fa-map-pin" },
    { key: "activityLog", label: t("Activity Log"), icon: "fa-clock-rotate-left" },
    { key: "emailLog", label: t("Correos y Notificaciones"), icon: "fa-envelope-open-text" },
    { key: "usage", label: t("Usage Statistics"), icon: "fa-chart-bar" },
    { key: "visitReview", label: t("Visit Review"), icon: "fa-route" },
    { key: "sync", label: t("Sync Center"), icon: "fa-rotate" },
    { key: "dispatch", label: t("Dispatch"), icon: "fa-truck-ramp-box" },
    { key: "salesBySeller", label: t("Sales by Seller"), icon: "fa-user-tie" },
    { key: "salesByMonth", label: t("Sales by Month"), icon: "fa-calendar-days" },
    { key: "topProductsBySeller", label: t("Top Products by Seller"), icon: "fa-boxes-stacked" },
    { key: "categoryDrilldown", label: t("Category Drilldown"), icon: "fa-layer-group" },
    { key: "byDependency", label: t("By Dependency / Zone"), icon: "fa-map-location-dot" },
  ]

  const renderTabContent = () => {
    if (activeTab === "overview") {
      return (
        <>
          <Row className="g-3 mb-4">
            {kpiCards.map((card, i) => (
              <Col xs={6} md={3} key={i}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="d-flex align-items-center gap-3">
                    <div
                      className={`rounded-circle d-flex align-items-center justify-content-center bg-${card.color} bg-opacity-10`}
                      style={{ width: 48, height: 48, flexShrink: 0 }}
                    >
                      <i className={`fa ${card.icon} text-${card.color}`} />
                    </div>
                    <div>
                      <div className="text-muted small">{card.label}</div>
                      {isLoadingStats ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <div className="fw-bold fs-5">{card.value?.toLocaleString() ?? "—"}</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom">
              <span className="fw-semibold small">
                <i className="fas fa-stethoscope me-2 text-muted" />
                {t("System Status")}
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              <Table className="mb-0 small" hover>
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">{t("Module")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Notes")}</th>
                    <th>{t("Route")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="ps-3 fw-semibold">{t("Activity Log")}</td>
                    <td><Badge bg="success"><i className="fas fa-circle-check me-1" />{t("OK")}</Badge></td>
                    <td className="text-muted">{t("Audit trail for account, place, incident, and platform events across all organizations.")}</td>
                    <td><a href={Routes.ActivityLog.path} className="small">/activity-log</a></td>
                  </tr>
                  <tr>
                    <td className="ps-3 fw-semibold">{t("Sales by Seller")}</td>
                    <td><Badge bg="success"><i className="fas fa-circle-check me-1" />{t("OK")}</Badge></td>
                    <td className="text-muted">{t("Calls dashboard/SalesBySellerGeneral. Year-comparison bar chart per seller.")}</td>
                    <td><a href={Routes.salesBySellerReport.path} className="small">/reports/salesBySeller</a></td>
                  </tr>
                  <tr>
                    <td className="ps-3 fw-semibold">{t("Sales by Month")}</td>
                    <td><Badge bg="success"><i className="fas fa-circle-check me-1" />{t("OK")}</Badge></td>
                    <td className="text-muted">{t("Calls dashboard/SalesByMonthGeneral.")}</td>
                    <td><a href={Routes.salesByMonthReport.path} className="small">/reports/salesByMonth</a></td>
                  </tr>
                  <tr>
                    <td className="ps-3 fw-semibold">{t("Dispatch Indicators")}</td>
                    <td><Badge bg="success"><i className="fas fa-circle-check me-1" />{t("OK")}</Badge></td>
                    <td className="text-muted">{t("Calls Indicators/Dispatch* endpoints. Superadmin must select an org before data loads.")}</td>
                    <td><a href={Routes.IndicatorsDispatch.path} className="small">/indicators/dispatch</a></td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Body>
              <IndicatorsPage embedded />
            </Card.Body>
          </Card>
        </>
      )
    }

    if (activeTab === "vehicleStops") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <VehicleStopsPage embedded />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "activityLog") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <ActivityLogPage embedded compact />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "emailLog") {
      return <EmailLogComponent />
    }

    if (activeTab === "usage") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <UsageStatisticsComponent />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "visitReview") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <VisitReviewComponent />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "sync") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <SyncMonitorComponent />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "dispatch") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <DispatchIndicatorsPage embedded />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "salesBySeller") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <SalesBySellerPage />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "salesByMonth") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <SalesByMonthPage />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "topProductsBySeller") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <DispatchTopProductsBySellerPage embedded />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "categoryDrilldown") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <DispatchCategoryDrilldownPage embedded />
          </Card.Body>
        </Card>
      )
    }

    if (activeTab === "byDependency") {
      return (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <DispatchByDependencyPage embedded />
          </Card.Body>
        </Card>
      )
    }

    return (
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <i className="fa fa-building me-2 text-muted" />
            {t("Organizations")}
          </span>
          <Form.Control
            size="sm"
            placeholder={`${t("Search")}...`}
            style={{ maxWidth: 220 }}
            value={orgsSearch}
            onChange={e => setOrgsSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") loadOrgs(0, orgsSearch) }}
          />
        </Card.Header>
        <Card.Body className="p-0">
          {isLoadingOrgs ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
          ) : (
            <Table hover responsive className="mb-0 small align-middle">
              <thead className="table-light">
                <tr>
                  <th>{t("Name")}</th>
                  <th className="text-center">{t("Places")}</th>
                  <th className="text-center">{t("Users")}</th>
                  <th className="text-center">{t("Vehicles")}</th>
                  <th className="text-end">{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {orgs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      {t("No organizations found")}
                    </td>
                  </tr>
                ) : orgs.map(org => (
                  <tr key={org.id}>
                    <td className="fw-semibold">
                      <div
                        className="text-primary clickable"
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedOrgForDetail(org)}
                      >
                        {org.name}
                      </div>
                    </td>
                    <td className="text-center"><Badge bg="light" text="dark">{org.totalPlaces}</Badge></td>
                    <td className="text-center"><Badge bg="light" text="dark">{org.totalUsers}</Badge></td>
                    <td className="text-center"><Badge bg="light" text="dark">{org.totalVehicles}</Badge></td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="px-2 py-0"
                        onClick={() => setSelectedOrgForDetail(org)}
                      >
                        <i className="fas fa-gear me-1" />
                        {t("Manage")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {orgsTotalPages > 1 && (
          <Card.Footer className="bg-white d-flex justify-content-between align-items-center">
            <Button variant="outline-secondary" size="sm"
              disabled={orgsPage === 0} onClick={() => loadOrgs(orgsPage - 1)}>
              <i className="fa fa-chevron-left" />
            </Button>
            <span className="small text-muted">
              {t("Page")} {orgsPage + 1} / {orgsTotalPages}
            </span>
            <Button variant="outline-secondary" size="sm"
              disabled={orgsPage + 1 >= orgsTotalPages} onClick={() => loadOrgs(orgsPage + 1)}>
              <i className="fa fa-chevron-right" />
            </Button>
          </Card.Footer>
        )}
      </Card>
    )
  }

  return (
    <Container fluid className="px-4 py-3">
      {/* Header */}
      <div className="d-flex align-items-center mb-4 gap-2">
        <i className="fas fa-shield-halved fa-lg text-warning" />
        <h4 className="mb-0 fw-bold">{t("Platform Admin")}</h4>
        <Badge bg="warning" text="dark" className="ms-1">{t("Superadmin")}</Badge>
      </div>

      <Row className="g-3">
        <Col xs={12} md={3} lg={2}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-2">
              <div className="nav flex-row flex-md-column nav-pills gap-1 overflow-auto flex-nowrap pb-2 pb-md-0">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    className={`nav-link text-start text-nowrap ${activeTab === tab.key ? "active" : ""}`}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    <i className={`fas ${tab.icon} me-2`} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={9} lg={10}>
          {renderTabContent()}
        </Col>
      </Row>

      {/* Organization Details Modal */}
      <OrgDetailsModal
        show={Boolean(selectedOrgForDetail)}
        org={selectedOrgForDetail}
        onHide={() => setSelectedOrgForDetail(null)}
        onRefresh={() => loadOrgs(orgsPage, orgsSearch)}
      />
    </Container>
  )
}

export default PlatformPage
